import { Facebook, Instagram, Mail, MapPin, Phone, Twitter } from "lucide-react";
import { Link } from "react-router-dom";
import { usePageContent, useSite } from "@/context/SiteContext";
import { imgUrl } from "@/lib/api";

export function Footer() {
  const { settings } = useSite();
  const footer = usePageContent("footer");
  const business = settings?.business || {};
  const social = settings?.social || {};
  const year = new Date().getFullYear();

  return (
    <footer className="border-t bg-card mt-24" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <p className="font-display text-lg text-primary">{business.name || "Eileen Jenkins Florist"}</p>
          {business.tagline && <p className="text-sm text-muted-foreground mt-1 italic">{business.tagline}</p>}
          {footer?.text && <p className="text-sm text-muted-foreground mt-3">{footer.text}</p>}
          <div className="flex gap-2 mt-4">
            {social.facebook && (
              <a href={social.facebook} target="_blank" rel="noreferrer" data-testid="social-facebook" aria-label="Facebook" className="p-2 rounded-full border hover:bg-primary/10 hover:text-primary transition-colors">
                <Facebook className="h-4 w-4" />
              </a>
            )}
            {social.instagram && (
              <a href={social.instagram} target="_blank" rel="noreferrer" data-testid="social-instagram" aria-label="Instagram" className="p-2 rounded-full border hover:bg-primary/10 hover:text-primary transition-colors">
                <Instagram className="h-4 w-4" />
              </a>
            )}
            {social.twitter && (
              <a href={social.twitter} target="_blank" rel="noreferrer" data-testid="social-twitter" aria-label="Twitter" className="p-2 rounded-full border hover:bg-primary/10 hover:text-primary transition-colors">
                <Twitter className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
        <div>
          <p className="font-semibold text-sm uppercase tracking-wider mb-4">Visit Us</p>
          <ul className="space-y-3 text-sm text-muted-foreground">
            {business.address && (
              <li className="flex gap-2">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                {business.map_link ? (
                  <a href={business.map_link} target="_blank" rel="noreferrer" className="hover:text-primary transition-colors underline-offset-4 hover:underline" data-testid="footer-address">
                    {business.address}
                  </a>
                ) : (
                  <span data-testid="footer-address">{business.address}</span>
                )}
              </li>
            )}
            {business.phone && (
              <li>
                <a href={`tel:${business.phone}`} className="flex gap-2 hover:text-primary transition-colors" data-testid="footer-phone">
                  <Phone className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> {business.phone}
                </a>
              </li>
            )}
            {business.email && (
              <li>
                <a href={`mailto:${business.email}`} className="flex gap-2 hover:text-primary transition-colors" data-testid="footer-email">
                  <Mail className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> {business.email}
                </a>
              </li>
            )}
          </ul>
        </div>
        <div>
          <p className="font-semibold text-sm uppercase tracking-wider mb-4">Opening Hours</p>
          <ul className="space-y-2 text-sm text-muted-foreground" data-testid="footer-hours">
            {(business.opening_hours || []).map((h, i) => (
              <li key={i} className="flex justify-between gap-4">
                <span>{h.day}</span>
                <span>{h.hours}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="font-semibold text-sm uppercase tracking-wider mb-4">Explore</p>
          <ul className="space-y-2 text-sm">
            {[
              ["Shop", "/shop"],
              ["Weddings", "/weddings"],
              ["Funeral Tributes", "/funeral-tributes"],
              ["Gallery", "/gallery"],
              ["Delivery", "/delivery"],
              ["Testimonials", "/testimonials"],
              ["FAQs", "/faqs"],
              ["About Us", "/about"],
            ].map(([label, to]) => (
              <li key={to}>
                <Link to={to} className="text-muted-foreground hover:text-primary transition-colors" data-testid={`footer-link-${label.toLowerCase().replace(/\s/g, "-")}`}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t py-5" data-testid="footer-copyright">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground text-center sm:text-left">
            © {year} {footer?.copyright || business.name || "Eileen Jenkins Florist"}. All rights reserved.
          </p>
          {settings?.branding?.accreditation_badge && (
            <div className="flex items-center gap-3" data-testid="footer-accreditation">
              <p className="text-xs text-muted-foreground text-right leading-tight">
                Proudly featured in<br />
                <span className="font-semibold text-foreground">The Good Florist Guide</span>
              </p>
              <img
                src={imgUrl(settings.branding.accreditation_badge)}
                alt="We are in The Good Florist Guide 2025–2026"
                className="h-16 w-16 sm:h-20 sm:w-20 object-contain"
                data-testid="gfg-badge-footer"
              />
            </div>
          )}
        </div>
      </div>
      <div className="bg-[#1B1512] text-[#EDE6DA]" data-testid="footer-credit">
        <div className="max-w-7xl mx-auto px-4 py-6 text-center">
          <p className="text-xs sm:text-sm font-medium tracking-[0.35em] uppercase">Built by Jac</p>
          <p className="mt-2.5 text-xs sm:text-sm flex items-center justify-center gap-3 flex-wrap">
            <a href="mailto:builtbyjac@gmail.com" className="hover:text-white transition-colors underline-offset-4 hover:underline" data-testid="credit-email">
              builtbyjac@gmail.com
            </a>
            <span className="opacity-40" aria-hidden="true">·</span>
            <a href="https://instagram.com/jacnewman08" target="_blank" rel="noreferrer" className="hover:text-white transition-colors underline-offset-4 hover:underline" data-testid="credit-instagram">
              @jacnewman08
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
